import { Button, Textarea } from "@heroui/react";
import React from "react";

import FirebaseAdapter from "@wc/adapters/firebase";
import { createCommentEvent } from "@wc/domain/teams/core/events-service";

import MentionAutocomplete from "./mention-autocomplete";

const MAX_CHARS = 100;

/**
 * Comment input component for adding comments to the timeline
 * @param {object} props Component props
 * @param {string} props.teamId The team ID
 * @param {string} props.weekOf The week identifier
 * @param {Array<{userId: string, displayName: string}>} props.teamMembers List of team members for mentions
 * @returns {React.ReactElement} Comment input UI
 */
export default function CommentInput({ teamId, weekOf, teamMembers = [] }) {
  const [text, setText] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showMentions, setShowMentions] = React.useState(false);
  const [mentionQuery, setMentionQuery] = React.useState("");
  const [cursorPosition, setCursorPosition] = React.useState(0);
  const textareaRef = React.useRef(null);

  const charsRemaining = MAX_CHARS - text.length;
  const isOverLimit = charsRemaining < 0;
  const canSubmit = text.trim().length > 0 && !isOverLimit && !isSubmitting;

  const handleInputChange = (value) => {
    setText(value);

    const cursorPos = textareaRef.current?.selectionStart || value.length;
    setCursorPosition(cursorPos);

    const textBeforeCursor = value.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowMentions(true);
    } else {
      setShowMentions(false);
      setMentionQuery("");
    }
  };

  const handleMentionSelect = (member) => {
    const textBeforeCursor = text.slice(0, cursorPosition);
    const textAfterCursor = text.slice(cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@\w*$/);

    if (mentionMatch) {
      const beforeMention = textBeforeCursor.slice(0, mentionMatch.index);
      const newText = `${beforeMention}@${member.displayName.replace(" ", "")} ${textAfterCursor}`;
      setText(newText);

      setTimeout(() => {
        const newCursorPos =
          beforeMention.length + member.displayName.replace(" ", "").length + 2;
        textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current?.focus();
      }, 0);
    }

    setShowMentions(false);
    setMentionQuery("");
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const userId = FirebaseAdapter().authn.getCurrentUser().uid;
      await createCommentEvent(teamId, userId, weekOf, text.trim());
      setText("");
    } catch (error) {
      console.error("Failed to submit comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !showMentions) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape" && showMentions) {
      setShowMentions(false);
    }
  };

  return (
    <div className="relative mt-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Textarea
            ref={textareaRef}
            value={text}
            onValueChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Add a comment... (use @ to mention)"
            size="sm"
            variant="bordered"
            minRows={2}
            maxRows={3}
            isDisabled={isSubmitting}
            classNames={{
              input: "text-sm",
            }}
            endContent={
              <span
                className={`absolute right-2 top-1 text-xs ${isOverLimit ? "text-danger" : "text-default-400"}`}
              >
                {charsRemaining}
              </span>
            }
          />
          {showMentions && (
            <MentionAutocomplete
              members={teamMembers}
              query={mentionQuery}
              onSelect={handleMentionSelect}
              onClose={() => setShowMentions(false)}
            />
          )}
        </div>
        <Button
          size="sm"
          color="primary"
          className="self-end"
          isDisabled={!canSubmit}
          isLoading={isSubmitting}
          onPress={handleSubmit}
        >
          Send
        </Button>
      </div>
    </div>
  );
}
